import type { SelectOptionType, CheckboxGroupType } from '~/presentation/reports/ui/type';
import type { IControlSettingUI, IDependentControl } from '../../type';
import type { IRItemCard, IRItemInfo } from '../types';

import { getDTimeSettingUI, getDimensionSettingUI } from '../utils';
import { EDimensionKey, EReportControlId } from '../types';
import { EDependentControlAction } from '../../type';

const VERSION_CONTROL_REQUIRES_CAMPAIGN_TAB: IDependentControl[] = [
  {
    id: EReportControlId.TAB_CAMPAIGN,
    action: EDependentControlAction.HIDE,
  },
];

const VERSION_OPTION_REQUIRES_CAMPAIGN_TAB: IDependentControl[] = [
  {
    id: EReportControlId.TAB_CAMPAIGN,
    action: EDependentControlAction.DISABLE,
    tooltip: {
      content: "To enable 'Version', please apply an 'Experiment' filter first.",
    },
  },
];

export const REPORT_DIMENSION_MAPPING: Record<
  EDimensionKey,
  {
    label: string;
    value: EDimensionKey;
  }
> = {
  [EDimensionKey.TIME]: {
    label: 'Time',
    value: EDimensionKey.TIME,
  },
  [EDimensionKey.VERSION]: {
    label: 'Version',
    value: EDimensionKey.VERSION,
  },
  [EDimensionKey.EXPERIMENT_ID]: {
    label: 'Experiment name',
    value: EDimensionKey.EXPERIMENT_ID,
  },
  [EDimensionKey.TRAFFIC_SOURCE]: {
    label: 'Traffic source',
    value: EDimensionKey.TRAFFIC_SOURCE,
  },
  [EDimensionKey.VISITOR]: {
    label: 'Visitor type',
    value: EDimensionKey.VISITOR,
  },
  [EDimensionKey.DEVICE]: {
    label: 'Device type',
    value: EDimensionKey.DEVICE,
  },
};

export const GENERAL_DIMENSION_INFO: IRItemCard = {
  title: 'Dimensions',
  content:
    'Attributes of your data — like traffic source or device — that let you look more granularly at aspects of your business. Dimensions show up as the rows of your data table.',
  helps: [],
};

export const DIMENSION_INFO: IRItemInfo = {
  [EDimensionKey.TIME]: {
    title: REPORT_DIMENSION_MAPPING[EDimensionKey.TIME].label,
    content: 'Timestamp the data was recorded',
    helps: [],
  },
  [EDimensionKey.VERSION]: {
    title: REPORT_DIMENSION_MAPPING[EDimensionKey.VERSION].label,
    dependentControls: VERSION_OPTION_REQUIRES_CAMPAIGN_TAB,
    content: 'Template version in an experiment that resulted in the order',
    helps: [],
  },
  [EDimensionKey.EXPERIMENT_ID]: {
    title: REPORT_DIMENSION_MAPPING[EDimensionKey.EXPERIMENT_ID].label,
    content: 'Experiments that resulted in the order',
    helps: [],
  },
  [EDimensionKey.TRAFFIC_SOURCE]: {
    title: REPORT_DIMENSION_MAPPING[EDimensionKey.TRAFFIC_SOURCE].label,
    content: 'Where your customers came from',
    helps: [],
  },
  [EDimensionKey.VISITOR]: {
    title: REPORT_DIMENSION_MAPPING[EDimensionKey.VISITOR].label,
    content: 'Target visitors who placed the order',
    helps: [],
  },
  [EDimensionKey.DEVICE]: {
    title: REPORT_DIMENSION_MAPPING[EDimensionKey.DEVICE].label,
    content: 'Device customers used to place the order',
    helps: [],
  },
};

const DIMENSION_OPTIONS: SelectOptionType[] = [
  {
    label: REPORT_DIMENSION_MAPPING[EDimensionKey.TIME].label,
    value: EDimensionKey.TIME,
  },
  {
    label: REPORT_DIMENSION_MAPPING[EDimensionKey.VERSION].label,
    value: EDimensionKey.VERSION,
    dependentControls: VERSION_OPTION_REQUIRES_CAMPAIGN_TAB,
  },
  // {
  //   label: REPORT_DIMENSION_MAPPING[EDimensionKey.EXPERIMENT_ID].label,
  //   value: EDimensionKey.EXPERIMENT_ID,
  // },
  {
    label: REPORT_DIMENSION_MAPPING[EDimensionKey.TRAFFIC_SOURCE].label,
    value: EDimensionKey.TRAFFIC_SOURCE,
  },
  {
    label: REPORT_DIMENSION_MAPPING[EDimensionKey.VISITOR].label,
    value: EDimensionKey.VISITOR,
  },
  {
    label: REPORT_DIMENSION_MAPPING[EDimensionKey.DEVICE].label,
    value: EDimensionKey.DEVICE,
  },
];

export const DIMENSION_GROUP: CheckboxGroupType = [
  {
    title: '',
    options: DIMENSION_OPTIONS,
  },
];

export const DIMENSION_UI: Record<string, IControlSettingUI> = {
  [EDimensionKey.TIME]: getDTimeSettingUI(),
  [EDimensionKey.DEVICE]: getDimensionSettingUI(
    EDimensionKey.DEVICE,
    REPORT_DIMENSION_MAPPING[EDimensionKey.DEVICE],
  ),
  [EDimensionKey.EXPERIMENT_ID]: getDimensionSettingUI(
    EDimensionKey.EXPERIMENT_ID,
    REPORT_DIMENSION_MAPPING[EDimensionKey.EXPERIMENT_ID],
  ),
  [EDimensionKey.TRAFFIC_SOURCE]: getDimensionSettingUI(
    EDimensionKey.TRAFFIC_SOURCE,
    REPORT_DIMENSION_MAPPING[EDimensionKey.TRAFFIC_SOURCE],
  ),
  [EDimensionKey.VISITOR]: getDimensionSettingUI(
    EDimensionKey.VISITOR,
    REPORT_DIMENSION_MAPPING[EDimensionKey.VISITOR],
  ),
  [EDimensionKey.VERSION]: getDimensionSettingUI(
    EDimensionKey.VERSION,
    REPORT_DIMENSION_MAPPING[EDimensionKey.VERSION],
    VERSION_CONTROL_REQUIRES_CAMPAIGN_TAB,
  ),
};
